import { FC, ReactNode } from 'react';
import { keyframes } from '@emotion/react';
import { alpha, Box, IconButton, LinearProgress, Paper, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { RemediationRunStatus } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import {
  ArrowLeftIcon,
  CheckIcon,
  ChecksIcon,
  ClockIcon,
  type Icon as PhosphorIcon,
  SparkleIcon,
  WarningIcon,
} from '@phosphor-icons/react';

import { type ItemProgress } from '../utils/run-progress';

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.4; }
  50%      { transform: scale(1.4); opacity: 1; }
`;

const ring = keyframes`
  0%   { transform: scale(1);    opacity: 0.45; }
  100% { transform: scale(1.25); opacity: 0; }
`;

/** Slides the buffer bar's stripes — motion without a claimed percentage. */
const drift = keyframes`
  from { background-position: 0 0; }
  to   { background-position: 17px 0; }
`;

/** Three breathing dots — the design's "the AI is thinking" cue. */
const AIPulse: FC = observer(() => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: theme.palette.ocean[700],
            animation: `${pulse} 1.4s ease-in-out ${i * 0.18}s infinite`,
          }}
        />
      ))}
    </Box>
  );
});

interface HeroProps {
  status: RemediationRunStatus;
  done: number;
  /** Documents in flight — shown as buffer, never counted as progress. */
  processing?: number;
  total: number;
  /** Sits on the title row, at the trailing edge — the elapsed pill. */
  trailing?: ReactNode;
  /** Renders the back control inside the hero, so it needs no strip of its own. */
  onBack?: () => void;
}

/**
 * The run hero: a status medallion, the headline for the current state, and —
 * while work is happening — an overall progress bar.
 *
 * The bar is a *buffer* bar, which is the honest shape for what we know: the
 * solid fill is documents the AI has finished, and the lighter buffer is the
 * ones it is writing now, whose progress within a document nothing reports. The
 * first version counted an in-flight document as half-done to keep the bar
 * moving — a number with nothing behind it.
 */
export const RunHero: FC<HeroProps> = observer(
  ({ status, done, processing = 0, total, trailing, onBack }) => {
    const theme = useTheme();
    const { t } = useTrans();

    const complete = status === RemediationRunStatus.COMPLETED;
    const failed = status === RemediationRunStatus.FAILED;
    const ready = status === RemediationRunStatus.READY_FOR_REVIEW;
    const working =
      status === RemediationRunStatus.PENDING ||
      status === RemediationRunStatus.PROCESSING ||
      status === RemediationRunStatus.FINALIZING;

    // Solid fill = settled. Buffer = settled + in flight. Nothing in between is
    // claimed, because nothing in between is reported.
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    const bufferPct = total > 0 ? Math.round(((done + processing) / total) * 100) : 0;

    const medallion: { bg: string; border: string; color: string; icon: PhosphorIcon } = complete
      ? {
          bg: theme.palette.green[200],
          border: theme.palette.green[300],
          color: theme.palette.green[800],
          icon: ChecksIcon,
        }
      : failed
        ? {
            bg: theme.palette.orange[200],
            border: theme.palette.orange[300],
            color: theme.palette.error.main,
            icon: WarningIcon,
          }
        : {
            bg: theme.palette.ocean[100],
            border: theme.palette.ocean[300],
            color: theme.palette.ocean[700],
            icon: ready ? ChecksIcon : SparkleIcon,
          };
    const Medallion = medallion.icon;

    const titleKey = complete
      ? 'app.remediation.final.title'
      : failed
        ? 'app.remediation.progress.failedTitle'
        : ready
          ? 'app.remediation.progress.readyTitle'
          : status === RemediationRunStatus.FINALIZING
            ? 'app.remediation.progress.finalizingTitle'
            : 'app.remediation.progress.title';

    const bodyKey = complete
      ? 'app.remediation.final.bodyNoVersion'
      : failed
        ? 'app.remediation.progress.failedBody'
        : ready
          ? 'app.remediation.progress.readyBody'
          : status === RemediationRunStatus.FINALIZING
            ? 'app.remediation.progress.finalizingSubtitle'
            : 'app.remediation.progress.subtitle';

    return (
      <Box
        sx={{
          px: 4,
          py: 3.5,
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
          flexShrink: 0,
          background: complete
            ? theme.palette.green[100]
            : failed
              ? theme.palette.orange[100]
              : `linear-gradient(180deg, ${theme.palette.ocean[100]} 0%, transparent 100%)`,
        }}
      >
        {/* Medallion, headline and the elapsed pill share one row — the pill is
          part of the header, not a strip floating above it. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 3,
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.25, flex: 1, minWidth: 280 }}
          >
            {/* Back sits inside the hero, sharing its gradient. Given its own strip
            it would open a band of bare white above the header. */}
            {onBack && (
              <IconButton
                onClick={onBack}
                sx={{
                  width: 34,
                  height: 34,
                  mt: 0.75,
                  flexShrink: 0,
                  backgroundColor: alpha(theme.palette.common.white, 0.7),
                  border: `1px solid ${theme.palette.grey[300]}`,
                  '&:hover': { backgroundColor: theme.palette.common.white },
                }}
              >
                <ArrowLeftIcon size={17} />
              </IconButton>
            )}
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 4,
                flexShrink: 0,
                position: 'relative',
                backgroundColor: medallion.bg,
                border: `1px solid ${medallion.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Medallion size={26} color={medallion.color} />
              {working && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: -3,
                    borderRadius: 4.5,
                    border: `2px solid ${theme.palette.ocean[700]}`,
                    animation: `${ring} 1.6s ease-out infinite`,
                  }}
                />
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  mb: 0.75,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="h6">{t(titleKey)}</Typography>
                {working && <AIPulse />}
              </Box>
              <Typography
                variant="textSM_regular"
                sx={{ color: 'text.secondary', maxWidth: 600, display: 'block' }}
              >
                {t(bodyKey)}
              </Typography>
            </Box>
          </Box>
          {trailing}
        </Box>

        {working && total > 0 && (
          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
              {t('app.remediation.progress.doneOf', { done, total })}
            </Typography>
            <Box
              sx={{
                flex: 1,
                height: 8,
                borderRadius: 99,
                overflow: 'hidden',
                position: 'relative',
                backgroundColor: theme.palette.grey[300],
              }}
            >
              {/* Buffer: in flight, progress unknown. Striped and animated so it
                reads as "working" without implying a percentage. */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: `${bufferPct}%`,
                  borderRadius: 99,
                  transition: 'width .4s ease',
                  backgroundImage: `repeating-linear-gradient(
                  -45deg,
                  ${alpha(theme.palette.ocean[700], 0.35)} 0 6px,
                  ${alpha(theme.palette.ocean[700], 0.15)} 6px 12px
                )`,
                  backgroundSize: '17px 100%',
                  animation: `${drift} 1s linear infinite`,
                }}
              />
              {/* Solid: actually settled. */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  height: '100%',
                  width: `${pct}%`,
                  borderRadius: 99,
                  transition: 'width .4s ease',
                  background: `linear-gradient(90deg, ${theme.palette.ocean[700]}, ${theme.palette.blue[700]})`,
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: theme.palette.ocean[700],
                minWidth: 36,
                textAlign: 'end',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {pct}%
            </Typography>
          </Box>
        )}
      </Box>
    );
  },
);

/** One compact count tile from the design's live-activity strip. */
const Stat: FC<{ label: string; value: number; color: string; icon: PhosphorIcon }> = observer(
  ({ label, value, color, icon: IconComponent }) => {
    const theme = useTheme();
    return (
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          minWidth: 110,
          px: 2,
          py: 1.75,
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: 3.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mb: 0.75,
            fontSize: 11.5,
            color: 'text.tertiary',
          }}
        >
          <IconComponent size={13} color={theme.palette.text.tertiary} />
          {label}
        </Box>
        <Typography sx={{ fontSize: 22, fontWeight: 600, color, lineHeight: 1 }}>
          {value}
        </Typography>
      </Paper>
    );
  },
);

/**
 * Live-activity counts, each one now moving on its own.
 *
 * "Processing" used to fold in every PENDING item on a working run, because the
 * backend never marked an item PROCESSING and counting them as queued would have
 * reported zero activity on a job that was plainly running. The run reports
 * per-document start and finish now, so the split is real: "Queued" is genuinely
 * waiting, "Processing" is genuinely being written, and "Complete" counts what
 * the AI has finished — including documents whose content is still travelling in
 * the run's result payload.
 */
export const RunStats: FC<{
  done: number;
  processing: number;
  queued: number;
  failed: number;
}> = observer(({ done, processing, queued, failed }) => {
  const theme = useTheme();
  const { t } = useTrans();

  return (
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
      <Stat
        label={t('app.remediation.progress.statDone')}
        value={done}
        color={theme.palette.green[700]}
        icon={CheckIcon}
      />
      <Stat
        label={t('app.remediation.progress.statProcessing')}
        value={processing}
        color={theme.palette.ocean[700]}
        icon={SparkleIcon}
      />
      <Stat
        label={t('app.remediation.progress.statQueued')}
        value={queued}
        color={theme.palette.text.secondary}
        icon={ClockIcon}
      />
      {/* Failed is the only conditional tile: a zero there is noise on a run
          that went fine, whereas the other three are the run's shape and stay
          put once it finishes. */}
      {failed > 0 && (
        <Stat
          label={t('app.remediation.progress.statFailed')}
          value={failed}
          color={theme.palette.error.main}
          icon={WarningIcon}
        />
      )}
    </Box>
  );
});

/**
 * Coloured extension tile, as in the design. Falls back to a neutral tile for
 * anything unrecognised — GENERATE items have no file yet, so they land here.
 */
export const FileTypeTile: FC<{ fileName?: string | null }> = observer(({ fileName }) => {
  const theme = useTheme();
  const ext = (fileName?.split('.').pop() ?? '').toLowerCase();

  const { bg, label } =
    ext === 'pdf'
      ? { bg: '#D93F2B', label: 'PDF' }
      : ext === 'xlsx' || ext === 'xls' || ext === 'csv'
        ? { bg: '#1D7044', label: 'XLSX' }
        : ext === 'docx' || ext === 'doc'
          ? { bg: '#1B5EBE', label: 'DOCX' }
          : { bg: theme.palette.grey[600], label: 'DOC' };

  return (
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: 2,
        flexShrink: 0,
        backgroundColor: bg,
        color: theme.palette.common.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 8.5,
        fontWeight: 800,
        letterSpacing: '0.02em',
      }}
    >
      {label}
    </Box>
  );
});

/**
 * One document's bar.
 *
 * Every state is measured: full for a document that is finished (whether its
 * content has landed yet or not), indeterminate while the AI is writing it,
 * empty while it waits its turn. This replaced a five-segment bar whose fill was
 * interpolated from elapsed time — those segments advanced on a timer regardless
 * of what the AI was doing. An indeterminate bar says "working, duration
 * unknown", which is the truth: the run reports when a document starts and
 * finishes, never how far through it is.
 */
export const DocumentPipeline: FC<{ progress: ItemProgress }> = observer(({ progress }) => {
  const theme = useTheme();

  const colour =
    progress.state === 'failed'
      ? theme.palette.error.main
      : progress.state === 'done'
        ? theme.palette.green[700]
        : theme.palette.ocean[700];

  if (progress.state === 'processing') {
    return (
      <LinearProgress
        sx={{
          height: 5,
          borderRadius: 99,
          backgroundColor: theme.palette.grey[300],
          '& .MuiLinearProgress-bar': { backgroundColor: colour },
        }}
      />
    );
  }

  const settled = progress.state === 'done' || progress.state === 'failed';
  return (
    <Box
      sx={{
        height: 5,
        borderRadius: 99,
        width: '100%',
        backgroundColor: theme.palette.grey[300],
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          height: '100%',
          width: settled ? '100%' : 0,
          borderRadius: 99,
          backgroundColor: colour,
          transition: 'width .4s ease',
        }}
      />
    </Box>
  );
});

/**
 * Header pill: how long the run has been going.
 *
 * This replaced a "~8–12 minutes remaining" estimate. That number was
 * extrapolated from a fixed minutes-per-document constant, and real runtime is
 * driven by LLM retry tails rather than document count — so it was routinely
 * off by multiples, and a countdown that stalls is worse than no countdown.
 * Elapsed time is measured, so it cannot be wrong.
 */
export const ElapsedTime: FC<{ hours: number; minutes: number }> = observer(
  ({ hours, minutes }) => {
    const theme = useTheme();
    const { t } = useTrans();

    return (
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1.75,
          py: 1.25,
          borderRadius: 3,
          flexShrink: 0,
          border: `1px solid ${theme.palette.grey[300]}`,
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: 2,
            backgroundColor: theme.palette.ocean[100],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <ClockIcon size={15} color={theme.palette.ocean[700]} />
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              color: 'text.tertiary',
              display: 'block',
              lineHeight: 1.4,
            }}
          >
            {t('app.remediation.progress.elapsedLabel')}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {hours > 0
              ? t('app.remediation.progress.elapsedHours', { hours, minutes })
              : t('app.remediation.progress.elapsedMinutes', { minutes })}
          </Typography>
        </Box>
      </Paper>
    );
  },
);

/**
 * The document rows' column widths, shared by the rows and the header strip
 * above them so the two cannot drift. Every track is fixed or a fraction of what
 * the fixed ones leave, which is what makes each row resolve to the same widths;
 * domain earns a column of its own only at lg, where there is width to spare.
 */
export const RUN_ITEM_GRID = {
  xs: '1fr',
  md: 'minmax(220px, 1.5fr) 160px minmax(200px, 1.4fr) 160px',
  lg: 'minmax(220px, 1.5fr) 130px 160px minmax(180px, 1.3fr) 160px',
} as const;

/** Gutters the rows and the header share, so their columns line up. */
export const RUN_ITEM_ROW_SX = { px: 2.5, gap: 2.25 } as const;

/**
 * Card shell for the document list, with the design's grey title strip.
 *
 * `header` is the column-name row and `footer` the pager; both sit outside
 * `children` so they stay put as the page of rows changes.
 */
export const DocumentListCard: FC<{
  title: string;
  count: string;
  /** Trailing control on the title strip — an operation on this list of documents. */
  action?: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}> = observer(({ title, count, action, header, footer, children }) => {
    const theme = useTheme();
    return (
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            backgroundColor: theme.palette.grey[200],
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: 11.5, color: 'text.tertiary' }}>{count}</Typography>
            {action}
          </Box>
        </Box>
        {header}
        {children}
        {/* No border of its own: a footer makes the last row stop being the
            card's last element, so that row keeps its own bottom rule and one
            here would draw a second line directly under it. */}
        {footer && <Box sx={{ px: 2.5, py: 1.5 }}>{footer}</Box>}
      </Paper>
    );
  },
);

/**
 * Column names for the document rows.
 *
 * Only from `md` up: the single-column stack below that has each value beside
 * its own icon and action, where a header row would be labelling nothing.
 */
export const DocumentListHeader: FC<{ labels: string[] }> = observer(({ labels }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        ...RUN_ITEM_ROW_SX,
        py: 1,
        display: { xs: 'none', md: 'grid' },
        gridTemplateColumns: RUN_ITEM_GRID,
        alignItems: 'center',
        backgroundColor: theme.palette.grey[100],
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
      }}
    >
      {labels.map((label, i) => (
        <Typography
          key={label || i}
          sx={{
            fontSize: 11,
            fontWeight: 600,
            color: 'text.tertiary',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            // The domain column exists only at lg, exactly as in the rows.
            ...(i === 1 ? { display: { xs: 'none', md: 'none', lg: 'block' } } : {}),
            // Actions sit at the trailing edge; its label follows them.
            ...(i === labels.length - 1 ? { textAlign: 'end' } : {}),
          }}
        >
          {label}
        </Typography>
      ))}
    </Box>
  );
});
